import { CalendarRange, Grid3X3, RefreshCcw } from "lucide-react";
import { useState } from "react";
import { SkeletonCard } from "@/components/skeleton";
import { useApi } from "@/hooks/use-api";
import { formatDateUtc, formatNumber } from "@/lib/i18n";
import { CohortTable } from "./cohort-table";

interface CohortRetentionCell {
  period: number;
  activeCustomers: number;
  retentionRate: number;
  revenueCents: number;
}

interface CohortRetentionRow {
  cohortStart: string;
  cohortSize: number;
  cells: CohortRetentionCell[];
}

interface CohortRetentionSnapshot {
  granularity: "week" | "month";
  from: string;
  to: string;
  periods: number[];
  rows: CohortRetentionRow[];
  generatedAt: string;
}

function formatWholePercent(value: number): string {
  return `${formatNumber(Math.round(value * 100), "en")}%`;
}

function getDefaultTo(): string {
  return new Date().toISOString().slice(0, 10);
}

function getDefaultFrom(): string {
  const date = new Date();
  date.setUTCMonth(date.getUTCMonth() - 5, 1);
  return date.toISOString().slice(0, 10);
}

export default function CohortAnalyticsPage() {
  const [granularity, setGranularity] = useState<"week" | "month">("month");
  const [dateRange, setDateRange] = useState(() => ({
    from: getDefaultFrom(),
    to: getDefaultTo(),
  }));

  const query = new URLSearchParams({
    from: dateRange.from,
    to: dateRange.to,
    granularity,
  }).toString();

  const { data, isLoading, error } = useApi<CohortRetentionSnapshot>(`/v1/analytics/cohorts?${query}`);

  const topRow = data?.rows[0];
  const monthOneCell = topRow?.cells.find((cell) => cell.period === 1) ?? null;

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--color-muted)] px-3 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
              <Grid3X3 className="h-3.5 w-3.5" />
              Tenant retention evidence
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Retention Cohorts</h1>
              <p className="mt-1 max-w-3xl text-sm text-[var(--color-muted-foreground)]">
                Tenant-authenticated cohort retention from ClickHouse purchase activity. This surface stays on the CRM analytics seam instead of the admin-only analytics worker.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 sm:min-w-[18rem] sm:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="text-[var(--color-muted-foreground)]">From</span>
              <input
                type="date"
                value={dateRange.from}
                onChange={(event) => setDateRange((current) => ({ ...current, from: event.target.value }))}
                className="min-h-11 w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-3 text-[var(--color-foreground)]"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-[var(--color-muted-foreground)]">To</span>
              <input
                type="date"
                value={dateRange.to}
                onChange={(event) => setDateRange((current) => ({ ...current, to: event.target.value }))}
                className="min-h-11 w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-3 text-[var(--color-foreground)]"
              />
            </label>
            <div className="sm:col-span-2">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-2">
                {(["month", "week"] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setGranularity(option)}
                    className={`min-h-11 rounded-xl border px-4 text-sm font-medium transition ${
                      granularity === option
                        ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                        : "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)]"
                    }`}
                  >
                    {option === "month" ? "Monthly cohorts" : "Weekly cohorts"}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <SkeletonCard key={index} className="h-40" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-6 text-sm text-[var(--color-error)]">
          Failed to load retention cohorts. {error}
        </div>
      ) : !data || data.rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-card)] p-12 text-center">
          <CalendarRange className="mx-auto mb-3 h-10 w-10 text-[var(--color-muted-foreground)]" />
          <p className="font-medium text-[var(--color-foreground)]">No qualifying cohorts in this window yet</p>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Cohorts appear after tenant purchase activity lands in ClickHouse for the selected date range.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.8fr)]">
          <CohortTable rows={data.rows} periods={data.periods} granularity={data.granularity} />

          <div className="space-y-4">
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
                <RefreshCcw className="h-4 w-4 text-[var(--color-primary)]" />
                Cohort Summary
              </div>
              <div className="mt-4 space-y-3">
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                    <div className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">Window</div>
                  <div className="mt-2 text-sm font-semibold text-[var(--color-foreground)]">
                    {formatDateUtc(data.from, "en")} to {formatDateUtc(data.to, "en")}
                  </div>
                </div>
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                  <div className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">Newest cohort</div>
                  <div className="mt-2 text-sm font-semibold text-[var(--color-foreground)]">{topRow ? formatDateUtc(topRow.cohortStart, "en") : "Unavailable"}</div>
                  <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    {topRow ? `${formatNumber(topRow.cohortSize, "en")} customers in the starting cohort.` : "No cohort data."}
                  </div>
                </div>
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
                  <div className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)]">
                    {granularity === "month" ? "Month 1 retention" : "Week 1 retention"}
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">
                    {monthOneCell ? formatWholePercent(monthOneCell.retentionRate) : "—"}
                  </div>
                  <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    Based on the newest visible cohort in the selected window.
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-card)] p-5 text-sm text-[var(--color-muted-foreground)]">
              The current `retention_cohorts_mv` definition is not yet reliable for real cohort math, so this route computes retention directly from tenant-scoped `attribution_events` until the MV basis is repaired.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
